/**
 * Vonage Call Cleanup Utility
 *
 * Handles graceful cleanup of call state when session errors occur.
 * Prevents cascading errors like "invalid-member-state" and "No call found".
 */

import type { VonageClient } from '@vonage/client-sdk';
import { logger } from '@/services/logger/logger';

export interface CallCleanupOptions {
  /** Whether to attempt hangup (false if session is already dead) */
  attemptHangup?: boolean;
  /** Reason for cleanup */
  reason: 'session_timeout' | 'session_error' | 'network_error' | 'manual';
  /** Additional context for logging */
  context?: Record<string, unknown>;
  /** The call ID to cleanup (undefined if no active call) */
  callId?: string;
}

/**
 * Safely cleanup call state when session becomes invalid
 */
export const cleanupVonageCallState = async (
  client: VonageClient,
  options: CallCleanupOptions,
): Promise<void> => {
  const { attemptHangup = false, reason, context = {}, callId } = options;

  logger.info('[VonageCallCleanup] Starting cleanup', { reason, attemptHangup, callId });

  if (!callId) {
    logger.info('[VonageCallCleanup] No active call to cleanup');
    return;
  }

  try {
    if (attemptHangup) {
      logger.info(`[VonageCallCleanup] Attempting hangup for call ${callId}`);
      await client.hangup(callId);
      logger.info(`[VonageCallCleanup] Successfully hung up call ${callId}`);
    } else {
      logger.info(`[VonageCallCleanup] Skipping hangup for call ${callId} (session invalid)`);
    }

    // Log cleanup completion
    logger.info('[VonageCallCleanup] Cleanup completed', {
      callId,
      attemptedHangup: attemptHangup,
      reason,
    });
  } catch (callError) {
    // Expected errors when session is invalid
    const errorMessage = callError instanceof Error ? callError.message : String(callError);
    const isExpectedError =
      errorMessage.includes('invalid-member-state') ||
      errorMessage.includes('No call found') ||
      errorMessage.includes('session') ||
      errorMessage.includes('timeout');

    if (!isExpectedError) {
      logger.error('[VonageCallCleanup] Unexpected error during call cleanup', {
        error: callError,
        callId,
      });

      logger.captureException(
        callError instanceof Error ? callError : new Error(String(callError)),
        {
          fingerprint: ['vonage-call-cleanup', reason],
          tags: {
            component: 'vonage_call_cleanup',
            cleanup_reason: reason,
          },
          level: 'warning',
          context: {
            callId,
            attemptHangup,
            errorMessage,
            ...context,
          },
        },
      );
    } else {
      logger.info(`[VonageCallCleanup] Expected error during cleanup: ${errorMessage}`);
    }
  }
};

/**
 * Helper to create a cleanup handler for session error handlers
 */
export const createCleanupHandler = (
  client: VonageClient,
  getCurrentCallId: () => string | undefined,
) => {
  return async (reason: CallCleanupOptions['reason']): Promise<void> => {
    const callId = getCurrentCallId();
    await cleanupVonageCallState(client, {
      attemptHangup: false, // Don't attempt hangup on session errors
      reason,
      callId,
    });
  };
};
